import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Plus,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    FileText,
    Calendar,
    TrendingUp,
    Upload,
    X,
    Info,
    CreditCard,
    Loader,
    Eye,
} from "lucide-react";
import {
    getMyAvanceRequestsRequest,
    getMyAvancesEnCoursRequest,
    createAvanceRequestRequest,
    cancelAvanceRequestRequest,
    clearAvanceRequestMessage,
} from "../../../../redux/actions/avanceRequestActions";
import { fetchAvanceRequest } from "../../../../redux/actions/avanceActions";
import "../MesrequetesStyles/MesAvances.css";
import Swal from 'sweetalert2';
import { useTranslation } from "react-i18next";
import useRequestHistoryAutoRefresh from "./useRequestHistoryAutoRefresh";
import { getRequestTracking } from "./requestWorkflowTracking";

const DEMO_MODE = false; // 👉 mets false pour repasser en mode normal

const mockUser = {
    id: 1,
    salary: 12000,
    salaireBase: 12000,
    seniority: "2 ans 3 mois",
    dateEmbauche: "2024-05-01",
};

const mockAvancesTypes = [
    {
        id: 1, type: "Avance sur salaire", categorie: "avance", actif: true,
        description: JSON.stringify({ couleur: "#3b82f6", plafondType: "pourcent", plafondValue: 30, mensualitesMin: 1, mensualitesMax: 1, dateDebut: ["M_IMMEDIAT", "M+1"], maxDemandes: 2, text: "Avance rapide déduite le mois même ou le mois suivant." }),
        tauxInteret: 0, nbMensualiteRemboursement: 1, typeJustificatif: "AUCUN", obligatoire: false, ancienneteMinimale: 0,
    },
    {
        id: 2, type: "Prêt", categorie: "pret", actif: true,
        description: JSON.stringify({ couleur: "#8b5cf6", plafondType: "fixe", plafondValue: 20000, mensualitesMin: 3, mensualitesMax: 12, dateDebut: ["M+1", "M+2"], maxDemandes: 1, text: "Prêt personnel remboursable sur plusieurs mois." }),
        tauxInteret: 0, nbMensualiteRemboursement: 12, montantMensualiteMinPercent: 5, montantMensualiteMaxPercent: 30,
        typeJustificatif: "PIECE_IDENTITE", obligatoire: true, ancienneteMinimale: 6,
    },
    {
        id: 3, type: "Prêt", categorie: "pret", actif: true,
        description: JSON.stringify({ couleur: "#10b981", plafondType: "fixe", plafondValue: 15000, mensualitesMin: 3, mensualitesMax: 24, dateDebut: ["M+1"], maxDemandes: 1, text: "Prêt social pour événement familial ou besoin spécifique." }),
        tauxInteret: 0, nbMensualiteRemboursement: 24, montantMensualiteMinPercent: 5, montantMensualiteMaxPercent: 25,
        typeJustificatif: "AUTRE", obligatoire: true, ancienneteMinimale: 12,
    },
    {
        id: 4, type: "Prêt", categorie: "pret", actif: true,
        description: JSON.stringify({ couleur: "#ef4444", plafondType: "fixe", plafondValue: 5000, mensualitesMin: 1, mensualitesMax: 6, dateDebut: ["M_IMMEDIAT"], maxDemandes: 1, text: "Prêt d'urgence pour besoin immédiat." }),
        tauxInteret: 0, nbMensualiteRemboursement: 6, typeJustificatif: "AUCUN", obligatoire: false, ancienneteMinimale: 0,
    },
];

const mockMyRequests = [
    {
        id: 101, avanceId: 1, avanceType: "Avance sur salaire", montantDemande: 3000, nombreMensualites: 1,
        montantMensuel: 3000, tauxInteret: 0, montantTotal: 3000, status: "PENDING_MANAGER", urgent: false,
        motif: "Besoin ponctuel", justificatifs: [], createdAt: "2026-07-28",
        managerDecision: null, managerName: null, managerDecisionAt: null, managerComment: null,
        adminDecision: null, adminName: null, adminDecisionAt: null, adminComment: null,
    },
    {
        id: 102, avanceId: 2, avanceType: "Prêt", montantDemande: 15000, nombreMensualites: 10,
        montantMensuel: 1500, tauxInteret: 0, montantTotal: 15000, status: "APPROVED", urgent: false,
        motif: "Achat véhicule", justificatifs: ["justificatifs/cin_101.pdf"], createdAt: "2026-06-10",
        managerDecision: true, managerName: "Karim Benali", managerDecisionAt: "2026-06-11", managerComment: "OK",
        adminDecision: true, adminName: "Amina Tazi", adminDecisionAt: "2026-06-12", adminComment: "Validé RH",
        statutRemboursement: "EN_COURS", mensualitesPayees: 4, montantRembourse: 6000, dateDebutRemboursement: "2026-07-01",
    },
    {
        id: 103, avanceId: 4, avanceType: "Prêt", montantDemande: 4000, nombreMensualites: 4,
        montantMensuel: 1000, tauxInteret: 0, montantTotal: 4000, status: "REJECTED", urgent: false,
        motif: "Réparation urgente", justificatifs: [], createdAt: "2026-05-15",
        managerDecision: false, managerName: "Karim Benali", managerDecisionAt: "2026-05-16",
        managerComment: "Demande similaire déjà en cours de remboursement.",
        adminDecision: null, adminName: null, adminDecisionAt: null, adminComment: null,
    },
];
const mockAvancesEnCours = mockMyRequests.filter((r) => r.status === "APPROVED" && r.statutRemboursement === "EN_COURS");


// En haut de votre fichier MesAvances.jsx, après l'import Swal
const MySwal = Swal.mixin({
    didOpen: () => {
        document.querySelector('.swal2-container').style.zIndex = '999999';
    }
});

const ECHEANCE_OPTIONS = [
    { value: "M_IMMEDIAT", label: "Même mois (M)", monthOffset: 0 },
    { value: "M+1", label: "Mois suivant (M+1)", monthOffset: 1 },
    { value: "M+2", label: "2 mois plus tard (M+2)", monthOffset: 2 },
    { value: "M+3", label: "3 mois plus tard (M+3)", monthOffset: 3 },
];

const normalizeEcheances = (value) => {
    if (Array.isArray(value)) {
        const clean = value.filter(Boolean);
        return clean.length > 0 ? clean : ["M+1"];
    }
    if (typeof value === "string" && value.trim()) {
        const trimmed = value.trim();
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                const clean = parsed.filter(Boolean);
                return clean.length > 0 ? clean : ["M+1"];
            }
            if (typeof parsed === "string") {
                return normalizeEcheances(parsed);
            }
        } catch (e) {}
        return trimmed.split(",").map(v => v.trim().replace(/^["'\[]+|["'\]]+$/g, "")).filter(Boolean);
    }
    return ["M+1"];
};

const parseAvanceDescription = (description) => {
    if (!description) return {};
    if (typeof description === "object") return description;

    try {
        const parsed = JSON.parse(description);
        if (typeof parsed === "string") {
            return parseAvanceDescription(parsed);
        }
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
        return {};
    }
};

const getEcheanceLabel = (value) => (
    ECHEANCE_OPTIONS.find(option => option.value === value)?.label || value
);

const getEcheanceHelp = (value) => {
    if (value === "M_IMMEDIAT") {
        return "Le prélèvement va commencer ce mois du salaire.";
    }

    const option = ECHEANCE_OPTIONS.find(item => item.value === value);
    const date = new Date();
    date.setMonth(date.getMonth() + (option?.monthOffset || 1));
    const month = date.toLocaleDateString("fr-FR", { month: "long" });
    const formattedMonth = month.charAt(0).toUpperCase() + month.slice(1);
    return `Le prélèvement commencera du mois de ${formattedMonth}.`;
};

const parseSeniorityToMonths = (seniority, hireDate) => {
    if (seniority !== undefined && seniority !== null) {
        const senStr = String(seniority).trim().toLowerCase();

        if (!Number.isNaN(Number(senStr))) {
            return Number(senStr);
        }

        const yearsMatch = senStr.match(/(\d+)\s*(?:an|ans|annee|annees)/);
        const monthsMatch = senStr.match(/(\d+)\s*mois/);
        const years = yearsMatch ? parseInt(yearsMatch[1], 10) : 0;
        const months = monthsMatch ? parseInt(monthsMatch[1], 10) : 0;

        if (years || months) {
            return (years * 12) + months;
        }
    }

    if (hireDate) {
        return Math.floor((new Date() - new Date(hireDate)) / (1000 * 60 * 60 * 24 * 30));
    }

    return 0;
};


const formatSeniority = (monthsValue) => {
    const totalMonths = Math.max(0, Math.floor(Number(monthsValue) || 0));
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    const parts = [];

    if (years > 0) {
        parts.push(`${years} ${years > 1 ? "ans" : "an"}`);
    }

    if (months > 0 || parts.length === 0) {
        parts.push(`${months} mois`);
    }

    return parts.join(" ");
};

const MesAvances = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    // Redux state
    const reduxAvanceRequests = useSelector((state) => state.avanceRequests);
    const reduxAvance = useSelector((state) => state.avance || { avances: [] });
    const reduxUser = useSelector((state) => state.auth.user);

    const loading = DEMO_MODE ? false : reduxAvanceRequests.loading;
    const myRequests = DEMO_MODE ? mockMyRequests : reduxAvanceRequests.myRequests;
    const myAvancesEnCours = DEMO_MODE ? mockAvancesEnCours : reduxAvanceRequests.myAvancesEnCours;
    const avances = DEMO_MODE ? mockAvancesTypes : reduxAvance.avances;
    const user = DEMO_MODE ? mockUser : reduxUser;

    const translateRequestType = (label) => {
        const value = String(label || "").trim();
        const normalized = value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const typeKeys = {
            "pret": "Prêt",
            "avance": "Avance",
            "avance sur salaire": "Avance sur salaire",
        };
        return t(typeKeys[normalized] || value, { defaultValue: value });
    };
    const translatedEcheanceLabel = (value) => t(getEcheanceLabel(value));

    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);

    const canCancelRequest = (request) => {
        const status = String(request?.status || "").toUpperCase();
        return (status === "PENDING_MANAGER" || status === "PENDING_ADMIN")
            && request?.managerDecision == null
            && request?.adminDecision == null;
    };



    const handleCancelRequest = (request) => {
        Swal.fire({
            icon: "warning",
            title: t("Annuler la demande ?"),
            text: t("Cette action est possible uniquement avant la première validation du workflow."),
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#64748b",
            confirmButtonText: t("Oui, annuler"),
            cancelButtonText: t("Retour"),
        }).then((result) => {
            if (result.isConfirmed) dispatch(cancelAvanceRequestRequest(request.id));
        });
    };

    const [formData, setFormData] = useState({
        avanceId: "",
        typeDemande: "",
        montant: "",
        mensualites: "",
        motif: "",
        urgence: false,
        justificatifs: [],  // ✅ TABLEAU
        requestType: "avance",
        echeance: "M+1",
    });

    // Charger les données au montage
    useEffect(() => {
        if (!DEMO_MODE) {
            dispatch(fetchAvanceRequest());
            dispatch(getMyAvanceRequestsRequest());
            dispatch(getMyAvancesEnCoursRequest());
        }
    }, [dispatch]);

    useRequestHistoryAutoRefresh(() => {
        if (!DEMO_MODE) {
            dispatch(getMyAvanceRequestsRequest({ silent: true }));
            dispatch(getMyAvancesEnCoursRequest({ silent: true }));
        }
    });

    const getCouleurByCategorie = (categorie) => {
        const colors = {
            AVANCE_SALAIRE: "#3b82f6",
            PRET_PERSONNEL: "#8b5cf6",
            PRET_SOCIAL: "#10b981",
            PRET_URGENCE: "#ef4444",
        };
        return colors[categorie] || "#6b7280";
    };

    // Calculer les stats du collaborateur
    const collaborateurStats = useMemo(() => {
        if (!user) return {
            anciennete: 0,
            ancienneteLabel: formatSeniority(0),
            demandesEnCours: 0,
        };

        const hireDate = user.dateEmbauche || user.hireDate;
        const anciennete = parseSeniorityToMonths(user.seniority, hireDate);

        const demandesEnCours = myRequests.filter(
            req => req.status === "PENDING_MANAGER" || req.status === "PENDING_ADMIN"
        ).length;

        return {
            anciennete,
            ancienneteLabel: formatSeniority(anciennete),
            demandesEnCours,
        };
    }, [user, myRequests]);

    // Filtrer les avances et prêts actifs
    const typesDemandes = useMemo(() => {
        return (avances || [])
            .filter(a => a && a.actif !== false && a.actif !== "false" && a.statut !== "INACTIF" && a.isActif !== false)
            .map(a => {
                const extra = parseAvanceDescription(a.description);
                return {
                    id: a.id,
                    nom: a.type,
                    categorie: a.categorie,
                    couleur: extra.couleur || getCouleurByCategorie(a.categorie),
                    plafondType: extra.plafondType || 'fixe',
                    montantMax: extra.plafondValue || 25000,
                    mensualitesMin: extra.mensualitesMin || 1,
                    mensualitesMax: extra.mensualitesMax || a.nbMensualiteRemboursement || 12,
                    montantMensualiteMinPercent: a.montantMensualiteMinPercent || null,
                    montantMensualiteMaxPercent: a.montantMensualiteMaxPercent || null,
                    tauxInteret: a.tauxInteret || 0,
                    echeancesAutorisees: normalizeEcheances(extra.dateDebut),
                    echeanceParDefaut: normalizeEcheances(extra.dateDebut).includes(extra.echeanceParDefaut)
                        ? extra.echeanceParDefaut
                        : normalizeEcheances(extra.dateDebut)[0],
                    maxDemandes: extra.maxDemandes || 1,
                    textDescription: extra.text || '',
                    justificatifRequis: a.typeJustificatif && a.typeJustificatif !== 'AUCUN',
                    obligatoire: a.obligatoire,
                    ancienneteMin: a.ancienneteMinimale || 0,
                };
            });
    }, [avances]);

    // Filtrer les types de demandes éligibles et non-éligibles selon l'ancienneté
    const eligibleTypes = useMemo(() => {
        return typesDemandes.filter((type) => collaborateurStats.anciennete >= type.ancienneteMin);
    }, [typesDemandes, collaborateurStats.anciennete]);

    const nonEligibleTypes = useMemo(() => {
        return typesDemandes.filter((type) => collaborateurStats.anciennete < type.ancienneteMin);
    }, [typesDemandes, collaborateurStats.anciennete]);

    const selectedType = useMemo(() => {
        return typesDemandes.find((t) => t.id === Number(formData.avanceId));
    }, [typesDemandes, formData.avanceId]);

    useEffect(() => {
        if (!showModal || !selectedType) return;
        const allowed = selectedType.echeancesAutorisees || ["M+1"];
        if (!allowed.includes(formData.echeance)) {
            setFormData((p) => ({ ...p, echeance: selectedType.echeanceParDefaut || allowed[0] || "M+1" }));
        }
    }, [showModal, selectedType, formData.echeance]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString(i18n.language, {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant) => {
        if (!montant) return "0";
        return new Intl.NumberFormat(i18n.language, {
            style: "decimal",
            minimumFractionDigits: 0
        }).format(montant);
    };

    const getSalaireNet = () => Number(user?.salary || user?.salaireNet || user?.salaireBase || user?.salaire || 0);
    const getSalaireBrut = () => Number(user?.salaireBrut || user?.grossSalary || user?.salaryBrut || user?.salaireBase || user?.salary || user?.salaire || 0);

    const getPlafondMontant = (type) => {
        if (!type) return 0;
        const plafondValue = Number(type.montantMax || 0);
        if (type.plafondType === "pourcent") {
            const salaireNet = getSalaireNet();
            return salaireNet > 0 ? (salaireNet * plafondValue) / 100 : null;
        }
        if (type.plafondType === "pourcent_brut") {
            const salaireBrut = getSalaireBrut();
            return salaireBrut > 0 ? (salaireBrut * plafondValue) / 100 : null;
        }
        return plafondValue;
    };

    const formatPlafond = (type) => {
        if (!type) return "0";
        if (type.plafondType === "pourcent") {
            return `${Number(type.montantMax || 0)} % ${t("du salaire net")}`;
        }
        if (type.plafondType === "pourcent_brut") {
            return `${Number(type.montantMax || 0)} % ${t("du salaire brut")}`;
        }
        return `${formatMontant(type.montantMax)} ${t("dhs")}`;
    };

    const calculateMensualite = () => {
        if (!formData.montant || !formData.mensualites || !formData.avanceId) return 0;
        const type = typesDemandes.find((t) => t.id === Number(formData.avanceId));
        if (!type) return 0;

        const montant = Number(formData.montant);
        const mois = Number(formData.mensualites);
        const taux = type.tauxInteret / 100 / 12;

        if (type.tauxInteret === 0) return montant / mois;
        return (montant * taux * Math.pow(1 + taux, mois)) / (Math.pow(1 + taux, mois) - 1);
    };

    const calculateMontantTotal = () => {
        return calculateMensualite() * Number(formData.mensualites || 0);
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files || []);
        setFormData((p) => ({
            ...p,
            justificatifs: [...p.justificatifs, ...files],
        }));
    };

    const removeFile = (index) => {
        setFormData((p) => ({
            ...p,
            justificatifs: p.justificatifs.filter((_, i) => i !== index),
        }));
    };

    const resetForm = () => {
        setFormData({
            avanceId: "",
            typeDemande: "",
            montant: "",
            mensualites: "",
            motif: "",
            urgence: false,
            justificatifs: [],
            requestType: "avance",
            echeance: "M+1",
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const type = typesDemandes.find((t) => t.id === Number(formData.avanceId));
        if (!type) return;

        // Validation montant
        const plafondMontant = getPlafondMontant(type);
        if (plafondMontant !== null && Number(formData.montant) > plafondMontant) {
            await Swal.fire({
                icon: 'error',
                title: t('Montant dépassé'),
                text: t('Le montant ne peut pas dépasser {{plafond}}.', { plafond: formatPlafond(type) }),
                confirmButtonColor: '#204385',
            });
            return;
        }

        // Validation nombre de demandes
        const demandesComptabilisees = (myRequests || []).filter((request) =>
            Number(request.avanceId) === Number(type.id)
            && request.status !== "REJECTED"
            && request.status !== "CANCELLED"
        ).length;
        if (type.maxDemandes && demandesComptabilisees >= Number(type.maxDemandes)) {
            await Swal.fire({
                icon: 'warning',
                title: t('Limite atteinte'),
                text: t('Vous avez dépassé le nombre de demandes autorisées pour ce type ({{maximum}}). Les demandes rejetées ne sont pas comptées dans cette limite.', { maximum: type.maxDemandes }),
                confirmButtonColor: '#204385',
            });
            return;
        }

        // Validation mensualités max
        if (Number(formData.mensualites) > type.mensualitesMax) {
            await Swal.fire({
                icon: 'error',
                title: t('Nombre de mensualités dépassé'),
                text: t('Le nombre de mensualités demandé dépasse le maximum autorisé pour ce type de demande ({{maximum}} {{mois}}).', { maximum: type.mensualitesMax, mois: t('mois') }),
                confirmButtonColor: '#204385',
            });
            return;
        }

        // Validation échéance
        if (!type.echeancesAutorisees.includes(formData.echeance)) {
            await Swal.fire({
                icon: 'warning',
                title: t('Échéance invalide'),
                text: t('Veuillez sélectionner une échéance autorisée pour ce type de demande.'),
                confirmButtonColor: '#204385',
            });
            return;
        }

        if (type.categorie !== 'avance') {
            // Validation mensualités min
            if (Number(formData.mensualites) < type.mensualitesMin) {
                await Swal.fire({
                    icon: 'error',
                    title: t('Nombre de mensualités insuffisant'),
                    text: t('Le nombre de mensualités doit être au minimum de {{minimum}} {{mois}}.', { minimum: type.mensualitesMin, mois: t('mois') }),
                    confirmButtonColor: '#204385',
                });
                return;
            }

            const salary = user?.salary || user?.salaireBase || user?.salaire || 10000;
            const mens = calculateMensualite();
            const pct = salary > 0 ? (mens / salary) * 100 : 0;

            if (type.montantMensualiteMaxPercent && pct > type.montantMensualiteMaxPercent) {
                await Swal.fire({
                    icon: 'error',
                    title: t('Mensualité trop élevée'),
                    html: t('La mensualité représente <b>{{pourcentage}}%</b> de votre salaire net, ce qui dépasse le maximum autorisé de <b>{{maximum}}%</b>.<br/><br/><span style="color:#64748b;font-size:12px">Salaire estimé : {{salaire}} {{devise}}</span>', { pourcentage: pct.toFixed(1), maximum: type.montantMensualiteMaxPercent, salaire: formatMontant(salary), devise: t('dhs') }),
                    confirmButtonColor: '#204385',
                });
                return;
            }

            if (type.montantMensualiteMinPercent && pct < type.montantMensualiteMinPercent) {
                await Swal.fire({
                    icon: 'warning',
                    title: t('Mensualité trop faible'),
                    html: t('La mensualité représente <b>{{pourcentage}}%</b> de votre salaire net, ce qui est inférieur au minimum requis de <b>{{minimum}}%</b>.<br/><br/><span style="color:#64748b;font-size:12px">Salaire estimé : {{salaire}} {{devise}}</span>', { pourcentage: pct.toFixed(1), minimum: type.montantMensualiteMinPercent, salaire: formatMontant(salary), devise: t('dhs') }),
                    confirmButtonColor: '#204385',
                });
                return;
            }
        }

        // Validation justificatifs
        if (type.obligatoire && type.justificatifRequis && formData.justificatifs.length === 0) {
            await Swal.fire({
                icon: 'warning',
                title: t('Justificatif requis'),
                text: t('Les justificatifs sont obligatoires pour ce type de demande. Veuillez joindre au moins un document.'),
                confirmButtonColor: '#204385',
            });
            return;
        }

        // Confirmation avant soumission
        const confirm = await Swal.fire({
            icon: 'question',
            title: t('Confirmer la demande'),
            html: t('Vous êtes sur le point de soumettre une demande de <b>{{montant}} {{devise}}</b> pour <b>{{type}}</b>.<br/>Cette demande sera transmise pour validation.', {
                montant: formatMontant(formData.montant),
                devise: t('dhs'),
                type: translateRequestType(type.nom || type.type),
            }),
            showCancelButton: true,
            confirmButtonColor: '#204385',
            cancelButtonColor: '#64748b',
            confirmButtonText: t('Oui, soumettre'),
            cancelButtonText: t('Annuler'),
        });

        if (!confirm.isConfirmed) return;

        const payload = {
            avanceId: Number(formData.avanceId),
            montantDemande: Number(formData.montant),
            nombreMensualites: Number(formData.mensualites),
            motif: formData.motif,
            urgent: formData.urgence,
            requestType: formData.requestType,
            echeance: formData.echeance,
            justificatifs: Array.isArray(formData.justificatifs) ? formData.justificatifs : [],
        };

        dispatch(createAvanceRequestRequest(payload));
        setShowModal(false);
        resetForm();

        setTimeout(() => {
            const histSection = document.getElementById("historique-section");
            if (histSection) histSection.scrollIntoView({ behavior: "smooth" });
        }, 1000);
    };
    const getStatutConfig = (statut) => {
        const config = {
            PENDING_MANAGER: { label: t("En attente du manager"), color: "#b45309", bg: "#fef3c7", border: "#fcd34d", dot: "#f59e0b" },
            PENDING_ADMIN:   { label: t("En attente de l'admin RH"), color: "#b45309", bg: "#fef3c7", border: "#fcd34d", dot: "#f59e0b" },
            APPROVED:        { label: t("Approuvée"), color: "#166534", bg: "#dcfce7", border: "#86efac", dot: "#16a34a" },
            REJECTED:        { label: t("Rejetée"), color: "#991b1b", bg: "#fee2e2", border: "#fca5a5", dot: "#ef4444" },
            CANCELLED:       { label: t("Annulée"), color: "#374151", bg: "#f3f4f6", border: "#d1d5db", dot: "#9ca3af" },
        };
        return config[statut] || { label: statut, color: "#374151", bg: "#f3f4f6", border: "#d1d5db", dot: "#9ca3af" };
    };

    const getStatutBadge = (statut, demande = null) => {
        const d = demande || selectedDemande;
        let c = getStatutConfig(statut);
        
        if (statut === "APPROVED" && d?.statutRemboursement === "EN_COURS") {
            c = { label: t("Approuvée"), color: "#075985", bg: "#e0f2fe", border: "#7dd3fc", dot: "#0ea5e9" };
        } else if (statut === "APPROVED" && d?.statutRemboursement === "TERMINE") {
            c = { label: t("Remboursé"), color: "#065f46", bg: "#d1fae5", border: "#6ee7b7", dot: "#059669" };
        }

        return (
            <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "3px 10px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                color: c.color,
                background: c.bg,
                border: `1px solid ${c.border}`,
                whiteSpace: "nowrap",
            }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
                {c.label}
            </span>
        );
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const selectRequestType = (type) => {
        if (!type) return;
        setFormData((p) => ({
            ...p,
            avanceId: type.id,
            typeDemande: type.nom,
            requestType: type.categorie === 'avance' ? 'avance' : 'pret',
            mensualites: type.categorie === 'avance' ? '1' : '',
            echeance: type.echeanceParDefaut || type.echeancesAutorisees?.[0] || "M+1",
        }));
    };

    const openModal = () => {
        resetForm();
        setShowModal(true);
    };

    // Pas de plafond annuel global

    if (loading && myRequests.length === 0) {
        return (
            <div className="maPage">
                <div className="maWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="maPage">
            <div className="maWrap">
                {/* Header */}
                <div className="maAdvanceHeader">
                    <div>
                        <h1>{t("Mes avances et prêts")}</h1>
                        <p>{t("Soumettez une nouvelle demande et suivez son avancement.")}</p>
                    </div>
                    <button type="button" className="maNewRequestBtn" onClick={openModal}>
                        <Plus size={15} />
                        {t("Nouvelle demande")}
                    </button>
                </div>

                {/* Stats */}
                <div className="maStats">
                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--pri">
                                <Clock size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">{t("Demandes en cours")}</div>
                                <div className="maStatValue maStatValue--pri">
                                    {collaborateurStats.demandesEnCours}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--info">
                                <TrendingUp size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">{t("En remboursement")}</div>
                                <div className="maStatValue maStatValue--pri">
                                    {myAvancesEnCours.length}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--ok">
                                <Calendar size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">{t("Ancienneté")}</div>
                                <div className="maStatValue maStatValue--pri">
                                    {collaborateurStats.ancienneteLabel}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 1: Types de demandes disponibles */}
                <div className="maCard maRequestTypes">
                    <div className="maSectionHead">
                        <div>
                            <div className="maSectionTitle">{t("Types de demandes disponibles")}</div>
                            <div className="maSectionSub">
                                {t("Sélectionnez le type d'avance ou de prêt qui correspond à votre besoin")}
                            </div>
                        </div>
                    </div>

                    {typesDemandes.length === 0 ? (
                        <div className="maEmpty">
                            <AlertCircle size={38} />
                            <p>{t("Aucun type d'avance disponible pour le moment")}</p>
                        </div>
                    ) : eligibleTypes.length === 0 ? (
                        <div className="maEmpty" style={{ borderColor: "#fca5a5", background: "#fef2f2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px" }}>
                            <AlertCircle size={38} style={{ color: "#ef4444" }} />
                            <p style={{ fontWeight: 800, color: "#991b1b", marginTop: 10, fontSize: "14px" }}>{t("Ancienneté minimale requise")}</p>
                            <p style={{ color: "#7f1d1d", fontSize: "12px", marginTop: 4, textAlign: "center" }}>
                                {t("Vous ne disposez pas de l'ancienneté requise pour effectuer une demande d'avance ou de prêt.")}
                                {" "}{t("Votre ancienneté actuelle est de")} <b>{collaborateurStats.ancienneteLabel}</b>.
                            </p>
                        </div>
                    ) : (
                        <div>
                            <div className="maGrid2">
                                {eligibleTypes.map((type) => (
                                    <div
                                        key={type.id}
                                        className="maTypeCard is-eligible"
                                        style={{ borderColor: type.couleur }}
                                        onClick={() => openModal(type)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <div className="maTypeTop">
                                            <div className="maTypeLeft">
                                                <div
                                                    className="maTypeIcon"
                                                    style={{
                                                        backgroundColor: `${type.couleur}20`,
                                                        color: type.couleur
                                                    }}
                                                >
                                                    <CreditCard size={18} />
                                                </div>
                                                <div>
                                                    <div className="maTypeName">{translateRequestType(type.nom)}</div>
                                                    <div
                                                        className="maPill"
                                                        style={{
                                                            backgroundColor: `${type.couleur}20`,
                                                            color: type.couleur
                                                        }}
                                                    >
                                                        {t(type.categorie === "avance" ? "Avance" : "Prêt")}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="maTypeDesc">{t(type.textDescription || type.nom, { defaultValue: type.textDescription || translateRequestType(type.nom) })}</div>

                                        <div className="maTypeGrid">
                                            <div>
                                                <div className="maMuted">{t("Plafond")}</div>
                                                <div className="maStrong" style={{ color: type.couleur }}>
                                                    {formatPlafond(type)}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="maMuted">{t("Durée max")}</div>
                                                <div className="maStrong">{type.mensualitesMax} {t("mois")}</div>
                                            </div>
                                            <div>
                                                <div className="maMuted">{t("Taux")}</div>
                                                <div className="maStrong">{type.tauxInteret}%</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Section d'information sur les produits bloqués en raison de l'ancienneté */}
                            {nonEligibleTypes.length > 0 && (
                                <div className="maTypeWarn" style={{ marginTop: 14, padding: "10px 12px", borderRadius: "8px", background: "#fffbeb", border: "1px solid #fde68a", color: "#b45309", display: "flex", alignItems: "center", gap: "8px" }}>
                                    <AlertCircle size={14} style={{ color: "#d97706" }} />
                                    <span>
                                        {t("Certains types de demandes ne sont pas affichés car ils requièrent une ancienneté supérieure à la vôtre")} (<b>{collaborateurStats.ancienneteLabel}</b>).
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/*<div className="maInfoBox">*/}
                    {/*    <Info size={18} />*/}
                    {/*    <div>*/}
                    {/*        <div className="maInfoTitle">Informations importantes</div>*/}
                    {/*        <ul className="maInfoList">*/}
                    {/*            <li>Les demandes suivent le workflow de validation configuré</li>*/}
                    {/*            <li>Les remboursements sont prélevés automatiquement sur le salaire</li>*/}
                    {/*            <li>Une seule demande en cours simultanément</li>*/}
                    {/*        </ul>*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                </div>

                {/* Section 2: Historique des demandes */}
                <div className="maCard" id="historique-section">
                    <div className="maSectionHead">
                        <div>
                            <div className="maSectionTitle">{t("Historique des demandes")}</div>
                        </div>
                        {myRequests.length > 0 && (
                            <span className="maHistoryCount">
                                {myRequests.length} {t("demande(s)")}
                            </span>
                        )}
                    </div>

                    {myRequests.length === 0 ? (
                        <div className="maEmpty">
                            <FileText size={38} />
                            <p>{t("Aucune demande enregistrée")}</p>
                        </div>
                    ) : (
                        <div className="maAdvanceTableWrap">
                            <table className="maAdvanceTable">
                                <thead>
                                    <tr>
                                        <th>{t("Référence / Date")}</th>
                                        <th>{t("Type de demande")}</th>
                                        <th>{t("Montant demandé")}</th>
                                        <th>{t("Mensualités")}</th>
                                        <th>{t("Mensualité")}</th>
                                        <th>{t("Taux")}</th>
                                        <th>{t("Statut")}</th>
                                        <th>Suivi</th>
                                        <th style={{ textAlign: "right" }}>{t("Actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {myRequests.map((d) => (
                                        <tr key={d.id}>
                                            <td>
                                                <div style={{ fontWeight: 700, color: "var(--admin-primary)" }}># {d.id}</div>
                                                <div className="maMuted">{formatDate(d.createdAt)}</div>
                                            </td>
                                            <td>
                                                <div className="maTypeCell">
                                                    <span 
                                                        className="maDot" 
                                                        style={{ backgroundColor: getCouleurByCategorie(d.avanceType) }} 
                                                    />
                                                    <span style={{fontWeight: 600}}>
    {translateRequestType(d.avanceType)}
</span>
                                                </div>
                                            </td>
                                            <td style={{ fontWeight: 700 }}>
                                                {formatMontant(d.montantDemande)} {t("dhs")}
                                            </td>
                                            <td>{d.nombreMensualites} {t("mois")}</td>
                                            <td style={{ fontWeight: 700, color: "var(--admin-accent)" }}>
                                                {formatMontant(d.montantMensuel)} {t("dhs")}
                                            </td>
                                            <td>{d.tauxInteret || 0}%</td>
                                            <td>{getStatutBadge(d.status, d)}</td>
                                            <td><span className="maMuted">{getRequestTracking(d).label}</span></td>
                                            <td style={{ textAlign: "right", display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}>
                                                <button
                                                    title={t("Détails")}
                                                    type="button"
                                                    onClick={() => showDetails(d)}
                                                    style={{
                                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                        width: 32, height: 32, padding: 0, borderRadius: 7,
                                                        border: "1px solid #e2e8f0", background: "#fff", color: "#475569",
                                                        cursor: "pointer", transition: "all .15s"
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--admin-accent)"; e.currentTarget.style.color = "var(--admin-accent)"; e.currentTarget.style.background = "#f0f4ff"; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#475569"; e.currentTarget.style.background = "#fff"; }}
                                                >
                                                    <Eye size={12} />
                                                </button>
                                                {canCancelRequest(d) && (
                                                    <button
                                                        title={t("Annuler la demande")}
                                                        type="button"
                                                        disabled={loading}
                                                        onClick={() => handleCancelRequest(d)}
                                                        style={{
                                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                            width: 32, height: 32, padding: 0, borderRadius: 7,
                                                            border: "1px solid #fca5a5", background: "#fff7f7", color: "#dc2626",
                                                            cursor: loading ? "not-allowed" : "pointer", transition: "all .15s",
                                                            opacity: loading ? 0.65 : 1
                                                        }}
                                                        onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#fef2f2"; }}
                                                        onMouseLeave={e => { e.currentTarget.style.background = "#fff7f7"; }}
                                                    >
                                                        <XCircle size={12} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL - Demande */}
            {showModal && (
                <div className="maModal">
                    <div className="maModal__panel">
                        <div className="maModal__header">
                            <div className="maModal__headLeft">
                                <div className="maModal__icon">
                                    <CreditCard size={18} />
                                </div>
                                <div>
                                    <div className="maModal__title">{t("Nouvelle demande")}</div>
                                    <div className="maModal__sub">{translateRequestType(formData.typeDemande)}</div>
                                </div>
                            </div>

                            <button
                                className="maIconBtn"
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                                aria-label={t("Fermer")}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="maModal__body">
                            <form onSubmit={handleSubmit}>
                                <div className="maField">
                                    <label>
                                        {t("Type de demande")} <span className="maReq">*</span>
                                    </label>
                                    <select
                                        value={formData.avanceId}
                                        onChange={(e) => selectRequestType(eligibleTypes.find((type) => type.id === Number(e.target.value)))}
                                        required
                                    >
                                        <option value="">{t("Choisissez un type d'avance ou de prêt")}</option>
                                        {eligibleTypes.map((type) => (
                                            <option key={type.id} value={type.id}>{translateRequestType(type.nom)}</option>
                                        ))}
                                    </select>
                                    {eligibleTypes.length === 0 && (
                                        <div className="maHelp">{t("Aucun type de demande n'est disponible pour votre ancienneté.")}</div>
                                    )}
                                </div>

                                {formData.avanceId && (
                                    <div className="maInfoInline">
                                        {(() => {
                                            const type = selectedType;
                                            if (!type) return null;
                                            return (
                                                <div className="maInlineGrid">
                                                    <div>
                                                        <div className="maMuted">{t("Plafond")}</div>
                                                        <div className="maStrong" style={{ color: type.couleur }}>
                                                            {formatPlafond(type)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">{t("Durée max")}</div>
                                                        <div className="maStrong">{type.mensualitesMax} {t("mois")}</div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">{t("Taux")}</div>
                                                        <div className="maStrong">{type.tauxInteret}%</div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                <div className="maFormGrid">
                                    <div className="maField">
                                        <label>
                                            {t("Montant demandé")} ({t("dhs")}) <span className="maReq">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.montant}
                                            onChange={(e) => setFormData((p) => ({ ...p, montant: e.target.value }))}
                                            required
                                        />
                                        <div className="maHelp">
                                            {t("Plafond")}:{" "}
                                            {formatPlafond(selectedType)}
                                        </div>
                                    </div>

                                    {selectedType?.categorie !== 'avance' && (
                                        <div className="maField">
                                            <label>
                                                {t("Nombre de mensualités")} <span className="maReq">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.mensualites}
                                                onChange={(e) => setFormData((p) => ({ ...p, mensualites: e.target.value }))}
                                                min={selectedType?.mensualitesMin || 1}
                                                max={selectedType?.mensualitesMax || 1}
                                                required
                                            />
                                            <div className="maHelp">
                                                {t("Min")} : {selectedType?.mensualitesMin || 1} | {t("Max")} :{" "}
                                                {selectedType?.mensualitesMax || 0}{" "}
                                                {t("mois")}
                                            </div>
                                        </div>
                                    )}

                                    {formData.avanceId && (
                                        <div className="maField">
                                            <label>
                                                {t("Échéance")} <span className="maReq">*</span>
                                            </label>
                                            <select
                                                value={formData.echeance}
                                                onChange={(e) => setFormData((p) => ({ ...p, echeance: e.target.value }))}
                                                required
                                            >
                                                {(selectedType?.echeancesAutorisees || ["M+1"]).map((echeance) => (
                                                    <option key={echeance} value={echeance}>
                                                        {translatedEcheanceLabel(echeance)}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="maHelp">{getEcheanceHelp(formData.echeance)}</div>
                                        </div>
                                    )}
                                </div>

                                {formData.montant && formData.mensualites && (
                                    <div className="maSimu">
                                        <div className="maSimuHead">
                                            <TrendingUp size={18} />
                                            {t("Simulation de remboursement")}
                                        </div>

                                        <div className="maSimuGrid">
                                            <div>
                                                <div className="maMuted">{t("Mensualité")}</div>
                                                <div className="maBig maAccent">
                                                    {formatMontant(calculateMensualite())} {t("dhs")}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="maMuted">{t("Total")}</div>
                                                <div className="maBig maPrimary">
                                                    {formatMontant(calculateMontantTotal())} {t("dhs")}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="maMuted">{t("Intérêts")}</div>
                                                <div className="maBig">
                                                    {formatMontant(calculateMontantTotal() - Number(formData.montant))} {t("dhs")}
                                                </div>
                                            </div>
                                            {selectedType?.categorie !== 'avance' && (
                                                <div>
                                                    <div className="maMuted">{t("Part du salaire")}</div>
                                                    <div className="maBig">
                                                        {(() => {
                                                            const salary = user?.salary || user?.salaireBase || user?.salaire || 10000;
                                                            const mens = calculateMensualite();
                                                            return salary > 0 ? `${((mens / salary) * 100).toFixed(1)}%` : "0%";
                                                        })()}
                                                    </div>
                                                    <div className="maHelp" style={{ marginTop: 2, fontSize: '10.5px', color: '#6b7280' }}>
                                                        {(() => {
                                                            const type = selectedType;
                                                            const parts = [];
                                                            if (type?.montantMensualiteMinPercent) parts.push(`Min: ${type.montantMensualiteMinPercent}%`);
                                                            if (type?.montantMensualiteMaxPercent) parts.push(`Max: ${type.montantMensualiteMaxPercent}%`);
                                                            return parts.length > 0 ? `(${parts.join(' | ')})` : '';
                                                        })()}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="maField">
                                    <label>{t("Motif (optionnel)")}</label>
                                    <textarea
                                        rows={4}
                                        value={formData.motif}
                                        onChange={(e)=> setFormData((p) => ({ ...p, motif: e.target.value }))}
                                        placeholder={t("Décrivez le motif de votre demande...")}
                                    />
                                </div>
                                {/**/}

                                {selectedType?.justificatifRequis && (
                                    <div className="maField">
                                        <label>
                                            {t("Justificatifs")}
                                            {selectedType?.obligatoire && (
                                                <span className="maReq"> *</span>
                                            )}
                                        </label>

                                        <div className="maDrop">
                                            <Upload size={28} />
                                            <div className="maDropText">{t("Glissez vos fichiers ici ou cliquez pour parcourir")}</div>
                                            <input id="maFile" type="file" multiple onChange={handleFileUpload} hidden />
                                            <label className="maBtn maBtn--outline" htmlFor="maFile">
                                                {t("Parcourir")}
                                            </label>
                                        </div>

                                        {formData.justificatifs.length > 0 && (
                                            <div className="maFiles">
                                                {formData.justificatifs.map((file, index) => (
                                                    <div key={index} className="maFileRow">
                                                        <span>{file.name || file}</span>
                                                        <button
                                                            type="button"
                                                            className="maFileX"
                                                            onClick={() => removeFile(index)}
                                                            aria-label={t("Supprimer")}
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="maModal__footer">
                                    <button
                                        type="button"
                                        className="maBtn maBtn--ghost"
                                        onClick={() => {
                                            setShowModal(false);
                                            resetForm();
                                        }}
                                    >
                                        {t("Annuler")}
                                    </button>
                                    <button type="submit" className="maBtn maBtn--accent" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <Loader size={16} className="spin" />
                                                {t("Envoi en cours...")}
                                            </>
                                        ) : (
                                            t("Soumettre")
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL - Détails */}
            {showDetailsModal && selectedDemande && (
                <div className="maModal">
                    <div className="maModal__panel maModal__panel--view">
                        <div className="maModal__header">
                            <div className="maAdvanceViewHead">
                                <div className="maModal__title">{translateRequestType(selectedDemande.avanceType || t("Demande d'avance"))}</div>
                                <div className="maAdvanceViewMeta">
                                    <span className="maAdvanceReference">#{selectedDemande.id}</span>
                                    {getStatutBadge(selectedDemande.status, selectedDemande)}
                                </div>
                            </div>

                            <button className="maIconBtn" onClick={() => setShowDetailsModal(false)} aria-label={t("Fermer")}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="maModal__body">
                            <div className="maAdvanceInfoPanel">
                                <div className="maAdvanceInfoTitle">{t("Informations de la demande")}</div>
                                <div className="maAdvanceInfoGrid">
                                    <div><span>{t("Date de demande")}</span><strong>{formatDate(selectedDemande.createdAt)}</strong></div>
                                    <div><span>{t("Montant demandé")}</span><strong>{formatMontant(selectedDemande.montantDemande)} {t("dhs")}</strong></div>
                                    <div><span>{t("Mensualités")}</span><strong>{selectedDemande.nombreMensualites} {t("mois")}</strong></div>
                                    <div><span>{t("Mensualité")}</span><strong>{formatMontant(selectedDemande.montantMensuel)} {t("dhs")}</strong></div>
                                    <div><span>{t("Taux")}</span><strong>{selectedDemande.tauxInteret || 0}%</strong></div>
                                    <div><span>{t("Montant total")}</span><strong>{formatMontant(selectedDemande.montantTotal)} {t("dhs")}</strong></div>
                                </div>
                            </div>

                            <div className="maBlock">
                                <div className="maBlockTitle">{t("Motif")}</div>
                                <div className="maBlockText">{selectedDemande.motif || "—"}</div>
                            </div>

                            {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 && (
                                <div className="maBlock">
                                    <div className="maBlockTitle">{t("Justificatifs")}</div>
                                    <div className="maPills">
                                        {selectedDemande.justificatifs.map((doc, idx) => (
                                            <span key={idx} className="maPill maPill--blue">
                                            {doc.split('/').pop()}
                                        </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedDemande.status === "APPROVED" && selectedDemande.statutRemboursement === "EN_COURS" && (
                                <div className="maBlock maBlock--soft">
                                    <div className="maBlockTitle">{t("Progression du remboursement")}</div>
                                    <div className="maMiniProgressTop">
                                    <span>
                                        {t("Payées")} : {selectedDemande.mensualitesPayees}/{selectedDemande.nombreMensualites}
                                    </span>
                                        <span className="maAccent">
                                        {t("Restant")} : {formatMontant(selectedDemande.montantTotal - selectedDemande.montantRembourse)} {t("dhs")}
                                    </span>
                                    </div>
                                    <div className="maProgressBar">
                                        <div
                                            className="maProgressFill is-ok"
                                            style={{
                                                width: `${(selectedDemande.mensualitesPayees / selectedDemande.nombreMensualites) * 100}%`
                                            }}
                                        />
                                    </div>
                                    {selectedDemande.dateDebutRemboursement && (
                                        <div className="maHelp" style={{ marginTop: 8 }}>
                                            {t("Début remboursement")} : {formatDate(selectedDemande.dateDebutRemboursement)}
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedDemande.status === "APPROVED" && selectedDemande.statutRemboursement === "TERMINE" && (
                                <div className="maBlock maBlock--soft">
                                    <div className="maBlockTitle">
                                        <CheckCircle size={18} style={{ color: "#10b981" }} />
                                        {t("Remboursement terminé")}
                                    </div>
                                    {selectedDemande.dateRemboursementComplet && (
                                        <div className="maHelp">
                                            {t("Complété le")} : {formatDate(selectedDemande.dateRemboursementComplet)}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Validation manager */}
                            {selectedDemande.managerDecision !== null && (
                                <div className="maBlock">
                                    <div className="maBlockTitle">{t("Validation manager")}</div>
                                    <div className="maDetailsLine">
                                        <span className="maMuted">{t("Décision")}:</span>{" "}
                                        <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                            {selectedDemande.managerDecision ? t("Approuvé") : t("Rejeté")}
                                        </b>
                                    </div>
                                    {selectedDemande.managerName && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">{t("Par")}:</span> <b>{selectedDemande.managerName}</b>
                                        </div>
                                    )}
                                    {selectedDemande.managerDecisionAt && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">{t("Date")}:</span> <b>{formatDate(selectedDemande.managerDecisionAt)}</b>
                                        </div>
                                    )}
                                    {selectedDemande.managerComment && (
                                        <div className="maReject" style={{ marginTop: 8 }}>
                                            <div className="maMuted">{t("Commentaire")}</div>
                                            <div className="maRejectText">{selectedDemande.managerComment}</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Validation admin */}
                            {selectedDemande.adminDecision !== null && (
                                <div className="maBlock">
                                    <div className="maBlockTitle">{t("Validation RH")}</div>
                                    <div className="maDetailsLine">
                                        <span className="maMuted">{t("Décision")}:</span>{" "}
                                        <b style={{ color: selectedDemande.adminDecision ? "#10b981" : "#ef4444" }}>
                                            {selectedDemande.adminDecision ? t("Approuvé") : t("Rejeté")}
                                        </b>
                                    </div>
                                    {selectedDemande.adminName && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">{t("Par")}:</span> <b>{selectedDemande.adminName}</b>
                                        </div>
                                    )}
                                    {selectedDemande.adminDecisionAt && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">{t("Date")}:</span> <b>{formatDate(selectedDemande.adminDecisionAt)}</b>
                                        </div>
                                    )}
                                    {selectedDemande.adminComment && (
                                        <div className="maReject" style={{ marginTop: 8 }}>
                                            <div className="maMuted">{t("Commentaire")}</div>
                                            <div className="maRejectText">{selectedDemande.adminComment}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="maModal__footer">
                            <button className="maBtn maBtn--ghost" onClick={() => setShowDetailsModal(false)}>
                                {t("Fermer")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default MesAvances;
